<script setup>
import { ref, computed, onMounted } from "vue";
import { useNotificacoesStore } from "@/stores/notificacoes";
import { storeToRefs } from "pinia";
import { formatarData } from "@/helpers/date";
import { useBreakpoint } from "@/composables/useBreakpoint";
import { useNotificacaoNavigation } from "@/composables/navigation/useNotificacaoNavigation";
import Panel from "primevue/panel";
import Button from "primevue/button";
import Badge from "primevue/badge";
import Card from "primevue/card";
import Divider from "primevue/divider";
import Paginator from "primevue/paginator";
import Dialog from "primevue/dialog";
import Loading from "./Loading.vue";
import MensagemResultado from "./MensagemResultado.vue";
import Header from "./Header.vue";

const props = defineProps({
    /**
     * Define se o componente será usado como página
     */
    page: {
        type: Boolean,
        default: false
    },
    /**
     * Define se o componente será compacto (Tamanho menor, fontes menores, sem bordas, etc)
     */
    compact: {
        type: Boolean,
        default: false
    },
    notificacaoId: {
        type: String,
        default: null
    },
    appendDialogTo: {
        type: String,
        default: 'body',
        description: 'ID do elemento ao qual o Dialog deve se limitar (elemento deve ter position: relative)'
    }
});

// NOTIFICAÇÕES
const notificacoesStore = useNotificacoesStore();

const { notificacoes, notificacoesNaoLidasCount, loading, erro, paginator } = storeToRefs(notificacoesStore);

// NAVEGAÇÃO
const { goToNotificacoes, showNotificacao } = useNotificacaoNavigation();

// DIALOG NOTIFICAÇÃO
const notificacoesMainBodyId = ref('notificacoes-main-body');

const dialogNotificacaoIsVisible = ref(false);
const notificacaoParaMostrar = ref(null);

// MENSAGEM RESULTADO
const tipoMensagemResultado = computed(() => {
    return notificacoes.value?.length === 0 ? 'sem-resultados' : erro ? 'erro' : '';
});

const tituloMensagemResultado = computed(() => {
    return tipoMensagemResultado.value === 'sem-resultados' ?
           'Você ainda não tem notificações' :
           tipoMensagemResultado.value === 'erro' ?
           'Não foi possível buscar notificações' :
           ''
});

const subTituloMensagemResultado = computed(() => {
    return tipoMensagemResultado.value === 'sem-resultados' ? ' ' : '';
});

// MOBILE/DESKTOP
const { isMobile } = useBreakpoint();
//

onMounted(async () => {
    await getNotificacoes();

    if (props.notificacaoId) {
        const notificacao = notificacoes.value.find(n => n.id === props.notificacaoId);

        if (notificacao) verNotificacao(notificacao);
    }
});

const getNotificacoes = async ({ page, rows } = { page: 0, rows: 10 }) => {
    await notificacoesStore.getNotificacoes({ page, rows });
}

const verNotificacao = (notificacao) => {
    if (!props.page) {
        showNotificacao(notificacao.id);

        return;
    }

    notificacaoParaMostrar.value = notificacao;
    dialogNotificacaoIsVisible.value = true;

    if (!notificacao.read_at) notificacoesStore.registrarNotificacaoComoLida(notificacao.id);
}

const getPanelRootStyle = () => {
    const panelRootStyle = {};

    panelRootStyle.height = props.page ? '100%' : '';
    panelRootStyle.flex = '1';
    panelRootStyle.display = 'flex';
    panelRootStyle.flexDirection = 'column';
    panelRootStyle.alignSelf = props.page ? '' : 'stretch';
    panelRootStyle.background = props.page ? 'var(--color-background)' : 'var(--color-surface)';

    if (props.page) {
        panelRootStyle.width = '100%';
        panelRootStyle.border = 'none';
    }

    if (props.compact) {
        panelRootStyle.borderRadius = '12px';
        panelRootStyle.border = 'none';
    }

    return panelRootStyle;
}

const getPanelHeaderStyle = () => {
    const panelHeaderStyle = {};

    panelHeaderStyle.background = 'var(--color-primary)';
    panelHeaderStyle.borderRadius = !props.page ? '8px 8px 0 0' : isMobile.value ? '0 0 8px 8px' : '0';
    panelHeaderStyle.padding = props.page ? '0' : props.compact ? '15px' : '20px';

    if (props.compact) {
        panelHeaderStyle.display = 'none';
    }

    return panelHeaderStyle;
}

const getCardBodyStyle = () => {
    const cardBodyStyle = {};

    if (props.page) {
        cardBodyStyle.padding = '30px';
    } else if (props.compact) {
        cardBodyStyle.padding = '15px';
    }

    return cardBodyStyle;
}
</script>

<template>
    <Panel
        :pt:root:style="getPanelRootStyle()"
        :pt:header:style="getPanelHeaderStyle()"
        pt:contentContainer:class="panel-content-container"
        pt:content:class="panel-content"
    >
        <template #header>
            <Header v-if="props.page" titulo="Notificações" :show-mobile-header="false" class="w-100"/>

            <div v-else class="w-100 d-flex align-items-center justify-content-between gap-2">
                <div class="d-flex align-items-center gap-2">
                    <Badge
                        v-if="notificacoesNaoLidasCount > 0 && !props.page"
                        :value="notificacoesNaoLidasCount"
                        severity="danger"
                        :size="props.compact ? 'small' : 'xlarge'"
                    />

                    <h2
                        :style="{
                            margin: 0,
                            color: '#ffffff',
                            fontWeight: 600,
                            fontSize: props.compact ? '1.3rem' : '2rem'
                        }"
                    >
                        Notificações
                    </h2>
                </div>
            </div>
        </template>

        <Loading :is-loading="loading" message="" class="justify" :style="{ margin: props.compact ? '0' : '' }"/>

        <MensagemResultado
            :visible="(erro || notificacoes?.length === 0) && !loading"
            :sub-titulo="subTituloMensagemResultado"
            :titulo="tituloMensagemResultado"
            :tipo="tipoMensagemResultado"
            class="justify"
            :style="{ margin: props.compact ? '0' : '' }"
        />

        <div
            :id="notificacoesMainBodyId"
            class="position-relative d-flex flex-column justify-content-end flex-grow-1"
            style="min-height: 0;"
        >
            <div
                v-if="notificacoes && notificacoes.length > 0 && !loading"
                class="d-flex flex-column flex-grow-1"
                :style="{
                    minHeight: '0',
                    padding: isMobile || !props.page ? '0' : '10px 20px 0 20px'
                }"
            >
                <div
                    v-if="props.page"
                    class="resultados-box"
                    :style="{
                        padding: isMobile ? '20px' : '10px 0',
                        background: isMobile ? '#ffffff' : 'transparent',
                        fontSize: isMobile ? '1.3rem' : '1.7rem'
                    }"
                >
                    <div class="d-flex align-items-center gap-1">
                        Caixa de Entrada
                        <span class="fw-light text-secondary">({{ paginator.totalRecords }})</span>
                    </div>

                    <span :class="['fw-light', isMobile ? 'fs-6' : 'fs-5']" style="color: var(--color-primary);">
                        {{ notificacoesNaoLidasCount }}
                        {{ notificacoesNaoLidasCount > 1 ? 'Notificações não lidas' : 'Notificação não lida' }}
                    </span>
                </div>

                <div class="d-flex flex-column flex-grow-1 overflow-auto" style="min-height: 0;">
                    <div
                        v-for="notificacao in notificacoes"
                        :key="notificacao.id"
                        class="d-flex flex-column"
                    >
                        <Card
                            pt:root:class="notificacao-card-root"
                            :pt:root:style="{
                                borderLeft: notificacao.read_at ? 'none' : '4px solid var(--color-secondary)'
                            }"
                            :pt:body:style="getCardBodyStyle()"
                            :pt:content:style="{
                                fontSize: props.page ? '1.3rem' : props.compact ? '0.875rem' : '1rem',
                                fontWeight: props.page ? '400' : '500'
                            }"
                            @click="verNotificacao(notificacao)"
                        >
                            <template #subtitle>
                                <div class="d-flex align-items-center gap-1">
                                    <i class="pi pi-calendar" style="margin-bottom: 3px;"/>
                                    {{ formatarData(notificacao.created_at) }}
                                </div>
                            </template>

                            <template #content>
                                {{ notificacao.data.message }}
                            </template>
                        </Card>

                        <Divider class="mx-3 my-0" style="width: unset !important;"/>
                    </div>
                </div>
            </div>

            <Paginator
                v-if="props.page && notificacoes?.length > 0"
                :rows="paginator.rows"
                :totalRecords="paginator.totalRecords"
                :first="paginator.first"
                :pageLinkSize="5"
                :rowsPerPageOptions="[10, 20, 50, 100]"
                @page="getNotificacoes($event)"
            />
        </div>

        <div
            v-if="!props.page"
            :class="props.compact ? 'p-2' : 'p-3'"
            style="background: #ffffff; border-radius: 0 0 8px 8px;"
        >
            <Button
                label="Ver todas"
                icon="pi pi-plus"
                icon-pos="right"
                severity="secondary"
                class="w-100"
                :size="props.compact ? 'small' : ''"
                @click="goToNotificacoes()"
            />
        </div>
    </Panel>

    <Dialog
        v-model:visible="dialogNotificacaoIsVisible"
        header="Notificação"
        modal
        :append-to="
            props.page ? `#${notificacoesMainBodyId}` :
            isMobile || props.appendDialogTo === 'body' ? 'body' :
            `#${props.appendDialogTo}`
        "
        :pt:mask:style="{ position: isMobile ? 'fixed' : 'absolute' }"
        :pt:root:style="{ width: isMobile ? '100%' : '80%' }"
    >
        <Card
            :pt:root:style="{ boxShadow: 'none', borderRadius: '0' }"
            :pt:body:style="{ padding: '8px' }"
            :pt:content:style="{ fontSize: '1.5rem' }"
        >
            <template #subtitle>
                <div class="d-flex align-items-center gap-1">
                    <i class="pi pi-calendar" style="margin-bottom: 3px;"/>
                    {{ formatarData(notificacaoParaMostrar.created_at) }}
                </div>
            </template>

            <template #content>
                {{ notificacaoParaMostrar.data.message }}
            </template>
        </Card>
    </Dialog>
</template>

<style scoped>
.resultados-box {
    margin: 10px 0;
    padding: 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    background: #ffffff;
    color: var(--color-text);
    font-size: 1.7rem;
    font-weight: 700;
    border-radius: 8px;
}

.justify {
    margin: 100px 0;
    flex: 1;
    display: flex;
    justify-content: center;
}

:deep(.panel-content-container) {
    min-height: 0 !important;
    flex: 1 !important;
    display: flex !important;
}

:deep(.panel-content) {
    display: flex !important;
    flex-direction: column !important;
    flex: 1 !important;
    padding: 0 !important;
}

:deep(.notificacao-card-root) {
    box-shadow: none;
    border-radius: 0;
    cursor: pointer
}

:deep(.notificacao-card-root:hover) {
    background: var(--color-primary-light);
}
</style>
